import api from './api';

const orderService = {
  /**
   * Get all orders with pagination and filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {string} params.orderStatus - Filter by order status
   * @param {string} params.paymentStatus - Filter by payment status
   * @param {string} params.startDate - Filter by start date (YYYY-MM-DD)
   * @param {string} params.endDate - Filter by end date (YYYY-MM-DD)
   * @param {string} params.service - Filter by service type (standard, semi-express, express)
   * @param {string} params.source - Filter by order source (Call, Walk-in, WhatsApp)
   * @returns {Promise} Orders data with pagination info
   */
  getOrders: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.orderStatus && params.orderStatus !== 'all') {
      queryParams.append('orderStatus', params.orderStatus);
    }
    if (params.paymentStatus && params.paymentStatus !== 'all') {
      queryParams.append('paymentStatus', params.paymentStatus);
    }
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.service && params.service !== 'all') {
      queryParams.append('service', params.service);
    }
    if (params.source && params.source !== 'all') {
      queryParams.append('source', params.source);
    }
    if (params.categories) queryParams.append('categories', params.categories);
    if (params.search) queryParams.append('search', params.search);

    const response = await api.get(`/api/orders?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Get a single order by ID
   * @param {string} orderId - The order ID (WX-XXXXX format)
   * @returns {Promise} Order data
   */
  getOrder: async (orderId) => {
    const response = await api.get(`/api/orders/${orderId}`);
    return response.data;
  },

  /**
   * Create a new order
   * @param {Object} orderData - Order details
   * @returns {Promise} Created order data
   */
  createOrder: async (orderData) => {
    const response = await api.post('/api/orders', orderData);
    return response.data;
  },

  /**
   * Update an existing order
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {Object} updateData - Fields to update
   * @returns {Promise} Updated order data
   */
  updateOrder: async (id, updateData) => {
    const response = await api.put(`/api/orders/${id}`, updateData);
    return response.data;
  },

  /**
   * Update order status
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {string} status - New order status
   * @returns {Promise} Updated order data
   */
  updateOrderStatus: async (id, status) => {
    const response = await api.put(`/api/orders/${id}`, { orderStatus: status });
    return response.data;
  },

  /**
   * Update payment status
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {string} paymentStatus - New payment status
   * @param {string} paymentMethod - Payment method (Cash, UPI)
   * @param {string} transactionID - Transaction ID for UPI payments
   * @returns {Promise} Updated order data
   */
  updatePaymentStatus: async (id, paymentStatus, paymentMethod = null, transactionID = null) => {
    const updateData = { paymentStatus };
    if (paymentMethod) updateData.paymentMethod = paymentMethod;
    if (transactionID) updateData.transactionID = transactionID;

    const response = await api.put(`/api/orders/${id}`, updateData);
    return response.data;
  },

  /**
   * Update order items (weight, subitems, etc.)
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {Array} items - Updated items array
   * @returns {Promise} Updated order data
   */
  updateOrderItems: async (id, items, addons) => {
    const payload = { items };
    // Explicit undefined means "don't touch addons"; [] clears them.
    if (addons !== undefined) payload.addons = addons;
    const response = await api.put(`/api/orders/${id}`, payload);
    return response.data;
  },

  /**
   * Apply discount to an order
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {number} discountPercentage - Discount percentage (0-100)
   * @param {number} discountAmount - Flat discount amount
   * @returns {Promise} Updated order data
   */
  applyDiscount: async (id, discountPercentage = 0, discountAmount = 0) => {
    const response = await api.put(`/api/orders/${id}`, {
      discountPercentage,
      discountAmount
    });
    return response.data;
  },

  /**
   * Assign pickup agent to an order
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {string} agentId - Staff/Agent MongoDB ID
   * @param {string} agentName - Agent name
   * @returns {Promise} Updated order data
   */
  assignPickupAgent: async (id, agentId, agentName) => {
    const response = await api.put(`/api/orders/${id}`, {
      pickupAgent: agentId,
      pickupAgentName: agentName
    });
    return response.data;
  },

  /**
   * Assign delivery agent to an order
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {string} agentId - Staff/Agent MongoDB ID
   * @param {string} agentName - Agent name
   * @returns {Promise} Updated order data
   */
  assignDeliveryAgent: async (id, agentId, agentName) => {
    const response = await api.put(`/api/orders/${id}`, {
      deliveryAgent: agentId,
      deliveryAgentName: agentName
    });
    return response.data;
  },

  /**
   * Schedule pickup for an order
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {Date} pickupDate - Pickup date
   * @param {string} pickupSlotId - Slot MongoDB ID
   * @returns {Promise} Updated order data
   */
  schedulePickup: async (id, pickupDate, pickupSlotId) => {
    const response = await api.put(`/api/orders/${id}`, {
      pickupDate,
      pickupSlot: pickupSlotId
    });
    return response.data;
  },

  /**
   * Schedule delivery for an order
   * @param {string} id - Order ID (WX-XXXXX format)
   * @param {Date} deliveryDate - Delivery date
   * @param {string} deliverySlotId - Slot MongoDB ID
   * @returns {Promise} Updated order data
   */
  scheduleDelivery: async (id, deliveryDate, deliverySlotId) => {
    const response = await api.put(`/api/orders/${id}`, {
      deliveryDate,
      deliverySlot: deliverySlotId
    });
    return response.data;
  },

  /**
   * Generate invoice PDF for an order
   * @param {string} orderId - Order ID (WX-XXXXX format)
   * @returns {Promise} PDF blob
   */
  generateInvoice: async (orderId) => {
    const response = await api.get(`/api/orders/${orderId}/invoice`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Generate manifest PDF for an order
   * @param {string} orderId - Order ID (WX-XXXXX format)
   * @returns {Promise} PDF blob
   */
  generateManifest: async (orderId) => {
    const response = await api.get(`/api/orders/${orderId}/manifest`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Manually trigger order status sync based on dates
   * Updates orders where pickup/delivery dates have arrived
   * @returns {Promise} Sync result
   */
  syncStatuses: async () => {
    const response = await api.post('/api/orders/sync-statuses');
    return response.data;
  },

  /**
   * Generate tag slips PDF for an order (small slips for tagging garments)
   * @param {string} orderId - Order ID (WX-XXXXX format)
   * @returns {Promise} PDF blob
   */
  generateTagSlips: async (orderId) => {
    const response = await api.get(`/api/orders/${orderId}/tags`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export default orderService;
